import { ref } from 'vue';
import { useStore } from 'vue-livestore';
import { tables, events } from '@/livestore/schema.js';
import { assertImportConfigRefs } from '@/livestore/referentialIntegrity.js';
import { importSheetValues } from '@/features/transfer/useSheetImport.js';
import { parseDateTime } from '@/domain/transfer/SheetImport.js';
import { filterNewRows } from '@/domain/sync/syncUtils.js';
import { googleDrive } from './providers/GoogleDriveProvider.js';

function filterNewSheetRows(existingRows, dataRows) {
  const existingKeys = existingRows.map(e => `${e.starts_at ?? ''}|${e.ends_at ?? ''}`);
  return filterNewRows(existingKeys, dataRows, row => {
    const clockInMs = parseDateTime(row[0]) ? new Date(parseDateTime(row[0])).getTime() : '';
    const clockOutMs = parseDateTime(row[1]) ? new Date(parseDateTime(row[1])).getTime() : '';
    return `${clockInMs}|${clockOutMs}`;
  });
}

export function useSheetSync() {
  const { store } = useStore();
  const pulling = ref(false);
  const lastError = ref<string | null>(null);

  function loadConfigs() {
    return store.query(tables.importConfigs.select());
  }

  function saveConfig(config) {
    if (!config.sheet_id) throw new Error('sheet_id required');
    assertImportConfigRefs(store, { workplace_id: config.workplace_id });
    const id = config.id || crypto.randomUUID();
    if (config.id) {
      store.commit(events.importConfigSaved({ ...config, id, updated_at: Date.now() }));
    } else {
      store.commit(
        events.importConfigSaved({ ...config, created_at: Date.now(), id, last_pulled_at: null }),
      );
    }
    return id;
  }

  function deleteConfig(id) {
    store.commit(events.importConfigDeleted({ id }));
  }

  async function pullConfig(config) {
    const values = await googleDrive.fetchSheetValues(config.sheet_id);

    const existing = store
      .query(tables.timeEntries.select().where({ is_deleted: 0 }))
      .filter(e => e.workplace_id === (config.workplace_id ?? ''));

    const header = values[0] ?? [];
    const dataRows = values.slice(1);
    const newRows = filterNewSheetRows(existing, dataRows);

    const result = await importSheetValues([header, ...newRows], {
      defaultWorkplaceId: config.workplace_id ?? null,
      strategy: 'skip' as const,
    });

    const last_pulled_at = Date.now();
    store.commit(events.importConfigPullTimestamped({ id: config.id, last_pulled_at }));
    return result;
  }

  async function pullAll() {
    pulling.value = true;
    lastError.value = null;
    const configs = loadConfigs();
    let totalImported = 0;
    for (const cfg of configs) {
      try {
        const { imported } = await pullConfig(cfg);
        totalImported += imported;
      } catch (e) {
        lastError.value = e.message;
      }
    }
    pulling.value = false;
    return { imported: totalImported };
  }

  return { deleteConfig, lastError, loadConfigs, pullAll, pullConfig, pulling, saveConfig };
}
